import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '../../../utils/util'
import { calculateDateRange } from '../../../utils/dateUtils'
import { getStudentPulseHistory } from '../../../server/server'
import { LoadingContainer } from './CheckInWidget'
import TrendsCard from '../pulseCheck/TrendsCard'
import { studentPulseStats, pulseStatus } from '../../../constants/PulseCheckAnalyticsConstants'
import InfoButton from '../../common/InfoButton'

function StudentPulseHistory({ daysFilter }) {
    const { t } = useTranslation()
    const query = useQuery()
    const studentId = query.get('id')
    const focus = query.get('focus')
    const [loading, setLoading] = useState(true)
    const [surveys, setSurveys] = useState([])
    const rootRef = useRef(null)
    const scrolledRef = useRef(false)

    useEffect(() => {
        if (!studentId || !daysFilter) return
        let active = true
        const load = async () => {
            setLoading(true)
            // Follow the shared day filter like every other analytics widget.
            const { startDate, endDate } = calculateDateRange(daysFilter)
            const resp = await getStudentPulseHistory(studentId, startDate, endDate)
            if (!active) return
            setSurveys(resp.status === 200 ? resp.data?.surveys || [] : [])
            setLoading(false)
        }
        load()
        return () => {
            active = false
        }
    }, [studentId, daysFilter])

    // When deep-linked from a pulse card, scroll this widget into view once.
    useEffect(() => {
        if (focus === 'pulse' && !loading && surveys.length > 0 && !scrolledRef.current && rootRef.current) {
            scrolledRef.current = true
            rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [focus, loading, surveys])

    const showLoading = useMemo(() => loading, [loading])

    // Hide the section entirely when the student has no survey history.
    if (!showLoading && surveys.length === 0) return null

    return (
        <div ref={rootRef} className="bg-white rounded-[24px] p-6 mt-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-darkenPurple text-[18px] font-bold">{t('Pulse Survey History')}</h3>
                    <p className="text-mudPurple text-[12px] font-medium">{t('This student’s survey responses over time')}</p>
                </div>
                <InfoButton text={t('Average response (1–5) per pulse run for each survey this student has answered.')} />
            </div>

            <div className="relative mt-4">
                {surveys.map((survey) => (
                    <SurveyHistoryBlock key={survey.survey_id} survey={survey} />
                ))}

                {showLoading && <LoadingContainer />}
            </div>
        </div>
    )
}

export default StudentPulseHistory

const SurveyHistoryBlock = ({ survey }) => {
    const { t } = useTranslation()
    const stats = useMemo(() => studentPulseStats({ cells: (survey.trend || []).map((p) => ({ overall: p.overall })) }), [survey])
    const status = pulseStatus(stats)
    const groups = (survey.groups || []).map((g) => g.group)

    return (
        <div className="mb-5 last:mb-0">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-3">
                    <span className="font-bold text-[15px] text-darkenPurple">{survey.title || t('Pulse survey')}</span>
                    <span className="px-[9px] py-[3px] rounded-full font-bold text-[10px]" style={{ background: status.bg, color: status.fg }}>
                        {t(status.label)}
                    </span>
                </div>
                <div className="flex items-center gap-4 text-[12px] font-medium text-mudPurple">
                    <span>
                        {t('Latest')}: <b className="text-darkenPurple">{stats.latest != null ? `${stats.latest.toFixed(1)}/5` : '—'}</b>
                    </span>
                    <span className={survey.summary?.delta >= 0 ? 'text-[#2B8C50]' : 'text-[#C73340]'}>
                        {survey.summary?.delta >= 0 ? '+' : ''}
                        {survey.summary?.delta} {t('vs start')}
                    </span>
                    <span>
                        {survey.summary?.runs} {t('runs')}
                    </span>
                </div>
            </div>
            <TrendsCard trend={survey.trend || []} groups={groups} height={240} />
        </div>
    )
}
